import { useState } from 'react';
import uniq from 'lodash/uniq';
import { useAuth } from '@guildeducationinc/guild-auth';
import { ApolloError, useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import {
  BOOKS_AND_FEES_ID,
  MemberPlan,
  Plan_EligibleProgram,
  Plan_Plan,
  Plan_ProductSet,
  UserPlanResponse,
  UserPlanVariables,
  USER_PLAN_QUERY,
} from '@src/graphql/home/queries/getUserPlan';

export function computeCoveredExpenses(products: Plan_ProductSet) {
  const productsWithCoveredExpenses = [
    products?.tuitionAssistance,
    products?.tuitionReimbursement,
    products?.sponsorship,
    products?.studentLoan,
  ];

  return !!productsWithCoveredExpenses.find((product) => {
    return (product?.fundingPackages || []).find(
      (fundingPackage) => (fundingPackage?.coveredExpenses || []).length > 0
    );
  });
}

export const computeBooksAndFees = (products: Plan_ProductSet): boolean => {
  const productsString = JSON.stringify(products);
  const hasBooksAndFees = productsString?.indexOf(BOOKS_AND_FEES_ID);
  if (hasBooksAndFees > 0) {
    return true;
  }
  return false;
};

export const computeNoCoverage = (products: Plan_ProductSet): boolean | undefined => {
  return (
    !products?.tuitionAssistance?.available &&
    !products?.tuitionReimbursement?.available &&
    !products?.sponsorship?.available
  );
};

export const computeTuitionReimbursementOnly = (products: Plan_ProductSet): boolean | undefined => {
  return (
    products?.tuitionReimbursement?.available &&
    !products?.tuitionAssistance?.available &&
    !products?.sponsorship?.available
  );
};

export const computeMemberPlan = (planData: UserPlanResponse): MemberPlan => {
  const { products } = planData?.userPlan?.plan || {};
  const {
    jobResources: careerAccelerator,
    tuitionAssistance,
    tuitionReimbursement,
    sponsorship,
    studentLoan,
  } = products || {};

  const memberPlan: MemberPlan = {
    hasTuitionAssistance: tuitionAssistance?.available,
    hasTuitionReimbursement: tuitionReimbursement?.available,
    hasSponsorship: sponsorship?.available,
    hasTuitionReimbursementOnly: computeTuitionReimbursementOnly(products),
    hasNoCoverage: computeNoCoverage(products),
    hasBooksAndFees: computeBooksAndFees(products),
    hasStudentLoanOptimization: studentLoan?.attributes?.optimizationAvailable,
    hasStudentLoanReimbursement: studentLoan?.attributes?.reimbursementAvailable,
    hasCareerAccelerator: careerAccelerator?.available,
    hasCoveredExpenses: computeCoveredExpenses(products),
  };
  return memberPlan;
};

export const getProgramIds = (plan: Plan_Plan): string[] => {
  const programIds: string[] = [];

  if (plan?.products?.tuitionAssistance?.programs.length) {
    const ids = plan?.products?.tuitionAssistance?.programs.map((program: Plan_EligibleProgram) => {
      return program.programId;
    });

    programIds.push(...ids);
  }

  if (plan?.products?.sponsorship?.programs.length) {
    const ids = plan?.products?.sponsorship?.programs.map((program: Plan_EligibleProgram) => {
      return program.programId;
    });

    programIds.push(...ids);
  }

  return programIds;
};

/**
 * @name useMemberPlan
 * @description A hook that gets user plan data and converts it to a MemberPlan object.
 * @returns An object containing loading, error, and a memberPlan.
 */
const useMemberPlan = () => {
  const { user } = useAuth();
  const [programIds, setProgramIds] = useState<string[]>([]);
  const employerId = user?.employerId ?? '';
  const userId = user?.id ?? '';

  const onCompleted = (data: UserPlanResponse) => {
    if (data?.userPlan?.plan) {
      const uniqProgramIds = uniq(getProgramIds(data?.userPlan?.plan));
      setProgramIds(uniqProgramIds);
    }
  };

  const onError = (error: ApolloError): void => {
    datadogRum.addError(error, {
      message: `There was an error getting user plan data: ${error.message}`,
    });
  };

  const { loading, error, data } = useQuery<UserPlanResponse, UserPlanVariables>(USER_PLAN_QUERY, {
    context: {
      server: Server.GLOBAL_APP_SYNC,
    },
    variables: {
      employerId,
      userId,
    },
    skip: !employerId || !userId,
    onCompleted,
    onError,
  });

  const memberPlan = data ? computeMemberPlan(data) : null;

  return {
    loading,
    error,
    memberPlan,
    programIds,
    planData: data,
  };
};

export default useMemberPlan;
